/**
 * Approval console API — рабочее место руководителя (задача 71).
 *
 * Read-роутер `/api/approval/*` под admin/лидом: выбранный юнит означает exact-сцену,
 * а `include_children=true` явно расширяет её до доступного актору поддерева:
 *  - `/week` — недельная сводка (два разреза по дню) для полосы-чипов (§5.1);
 *  - `/day` — лента сданных отчётов дня (§5.2);
 *  - `/missing` — несдавшие за день для плашки (§5.3).
 *
 * `unit_id` опционален на wire: host выбирает единственный юнит лида, admin обязан
 * указать его явно. Все эндпоинты типизированы из `api/types.ts` (generate-types).
 */
import { api } from "./client";
import type { components } from "./types";

export type WeekSummary = components["schemas"]["WeekSummary"];
export type WeekDaySummary = components["schemas"]["WeekDaySummary"];
export type DayChipState = components["schemas"]["DayChipState"];
export type DayReportFeed = components["schemas"]["DayReportFeed"];
export type DayReportCard = components["schemas"]["DayReportCard"];
export type DayReportEntry = components["schemas"]["DayReportEntry"];
export type MissingList = components["schemas"]["MissingList"];
export type MissingSubject = components["schemas"]["MissingSubject"];
export type TimesheetApprovalState = components["schemas"]["TimesheetApprovalState"];
export type WeekFeed = components["schemas"]["WeekFeed"];
export type WeekUnitGroup = components["schemas"]["WeekUnitGroup"];
export type WeekPersonRow = components["schemas"]["WeekPersonRow"];
export type WeekDayCell = components["schemas"]["WeekDayCell"];
export type WeekDayState = components["schemas"]["WeekDayState"];
export type WeekConfirmationState = components["schemas"]["WeekConfirmationState"];
export type WeekBlockingReason = components["schemas"]["WeekBlockingReason"];
export type WeekBlockingReasonKind = components["schemas"]["WeekBlockingReasonKind"];
export type WeekProjectWorkRow = components["schemas"]["WeekProjectWorkRow"];
export type WeekApproveOutcome = components["schemas"]["WeekApproveOutcome"];
export type WeekApproveProblem = components["schemas"]["WeekApproveProblem"];
export type WeekBulkApproveResult = components["schemas"]["WeekBulkApproveResult"];

function withScene(
  params: URLSearchParams,
  unitId: number | null,
  includeChildren: boolean,
): URLSearchParams {
  if (unitId !== null) {
    params.set("unit_id", String(unitId));
  }
  if (includeChildren) params.set("include_children", "true");
  return params;
}

export function getWeekSummary(
  weekStart: string,
  unitId: number | null,
  includeChildren = false,
): Promise<WeekSummary> {
  const params = withScene(new URLSearchParams({ week_start: weekStart }), unitId, includeChildren);
  return api.get<WeekSummary>(`/approval/week?${params.toString()}`);
}

export function getDayFeed(
  date: string,
  unitId: number | null,
  includeChildren = false,
): Promise<DayReportFeed> {
  const params = withScene(new URLSearchParams({ date }), unitId, includeChildren);
  return api.get<DayReportFeed>(`/approval/day?${params.toString()}`);
}

export function getMissing(
  date: string,
  unitId: number | null,
  includeChildren = false,
): Promise<MissingList> {
  const params = withScene(new URLSearchParams({ date }), unitId, includeChildren);
  return api.get<MissingList>(`/approval/missing?${params.toString()}`);
}

/**
 * Подтверждение / отмена отчёта (задача 71 §1.1) — write-эндпоинты живут в
 * backfill-роутере `/admin/timesheet/*` (actor=session-cookie), не в read-only
 * `/approval/*`. По (user_id, date), не по timesheet-id.
 */
function approvalQs(userId: number, date: string): string {
  return `?user_id=${userId}&date=${encodeURIComponent(date)}`;
}

/**
 * Подтвердить отчёт за (user_id, date). С `declaredHalf` — combined-финал «табель
 * + approve» одним действием (задача 79 §1.1): табель пишется перед approve,
 * `signed_at` не трогается. Без аргумента — прежнее по-отчётное подтверждение.
 */
export function approveTimesheet(
  userId: number,
  date: string,
  declaredHalf?: number | null,
): Promise<TimesheetApprovalState> {
  const body =
    declaredHalf === undefined || declaredHalf === null
      ? undefined
      : { declared_total_hours_half: declaredHalf };
  return api.post<TimesheetApprovalState>(
    `/admin/timesheet/approve${approvalQs(userId, date)}`,
    body,
  );
}

export function unapproveTimesheet(userId: number, date: string): Promise<TimesheetApprovalState> {
  return api.post<TimesheetApprovalState>(`/admin/timesheet/unapprove${approvalQs(userId, date)}`);
}

/**
 * Недельная лента подтверждения (задача 116 §1.2). По умолчанию содержит exact
 * выбранного weekly-юнита; `includeChildren` явно добавляет доступное актору
 * поддерево. Admin обязан выбрать юнит.
 */
export function getWeekFeed(
  weekStart: string,
  unitId: number | null,
  includeChildren = false,
): Promise<WeekFeed> {
  const params = withScene(new URLSearchParams({ week_start: weekStart }), unitId, includeChildren);
  return api.get<WeekFeed>(`/approval/week-feed?${params.toString()}`);
}

/**
 * Подтвердить неделю одного человека (задача 116 §1.3) — массовый дневной approve
 * его `signed`-отчётов. 422 (строкой) при пустом рабочем дне; фронт блокирует CTA
 * заранее, но ловит ошибку на гонке.
 */
export function approveWeek(userId: number, weekStart: string): Promise<WeekApproveOutcome> {
  return api.post<WeekApproveOutcome>(`/approval/approve-week`, {
    user_id: userId,
    week_start: weekStart,
  });
}

/**
 * «Подтвердить всех» за неделю (задача 116 §1.3) — подтверждает только «чистых»
 * людей, возвращает структурированный итог для попапа 3b. Мутация получает тот
 * же exact/subtree scope, который показан в ленте.
 */
export function approveWeekBulk(
  weekStart: string,
  unitId: number,
  includeChildren = false,
): Promise<WeekBulkApproveResult> {
  return api.post<WeekBulkApproveResult>(`/approval/approve-week-bulk`, {
    week_start: weekStart,
    unit_id: unitId,
    ...(includeChildren ? { include_children: true } : {}),
  });
}
